import {
  CalendarMonth,
  ChevronLeft,
  FmdGood,
  LocalOfferOutlined,
  Person,
} from "@mui/icons-material";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import DatePickerModal from "app/components/Form/CalendarModal";
import { selectItems } from "lib/features/item/itemsSlice";
import {
  BookableExperienceAvailabilityItem,
  ItemBookableExperience,
} from "lib/features/item/types";
import { useCreateReferralMutation } from "lib/features/referral/referralService";
import { User } from "lib/features/user/types";
import { selectUser } from "lib/features/user/userSlice";
import { useAppSelector } from "lib/hooks";
import moment from "moment";
import React, { ReactElement, useEffect } from "react";
import { Link, Params, useNavigate, useParams } from "react-router-dom";
import { getGoogleMapsLink } from "util/getGoogleMapsLink";

import InfoSection from "app/components/InfoSection";
import { useGetBookableExperienceAvailabilityByItemIdQuery } from "lib/features/bookableExperience/bookableExperienceService";
import { removeExpiredTimes } from "util/filterBookableExperiences";

const InfoBox: React.FC<{
  children?: any;
  icon: ReactElement;
  label: string;
}> = ({ children, icon, label }) => (
  <Box display="flex" flexDirection={"row"} alignItems={"center"} gap="1rem">
    {icon}
    <Box>
      <Typography
        variant={"body1"}
        sx={{ fontSize: "20px", fontWeight: "bold" }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  </Box>
);

// This component uses route params timeId and itemId
// and updates its own state according to those values
const BookableItemPage: React.FC = () => {
  const navigate = useNavigate();
  const items: BookableExperienceAvailabilityItem[] =
    useAppSelector(selectItems);

  const user: User = useAppSelector(selectUser);

  const { itemId, timeId } = useParams<Params>();

  const [datePickerOpen, setDatePickerOpen] = React.useState(false);
  const [referralLink, setReferralLink] = React.useState<string | null>(null);

  const [currentItem] = React.useState<
    BookableExperienceAvailabilityItem | undefined
  >(items.find((item) => item.id === parseInt(itemId || "")));

  // currentBookableExperience is initialized by removing any expired times (like if there are times from earlier today)
  // then finding the current time based on the timeId in the url, or defaulting to the first one.
  const [currentBookableExperience, setCurrentBookableExperience] =
    React.useState<ItemBookableExperience | undefined>(
      removeExpiredTimes(currentItem?.itemBookableExperience).filter(
        (time, idx: number) =>
          timeId ? time.id === parseInt(timeId) : idx === 1
      )[0]
    );

  // todaysBookableExperiences is the thing that gets iterated over to show the time buttons.
  // here, we  strip out any experiences that have passed
  // and find the ones that are on the same day as the current time, specified by the timeId in the url
  const [todaysBookableExperiences, setTodaysBookableExperiences] =
    React.useState<ItemBookableExperience[] | undefined>(
      removeExpiredTimes(currentItem?.itemBookableExperience).filter(
        (time: ItemBookableExperience) =>
          moment(time.start_date).format("yyyy-MM-DD") ===
          moment(currentBookableExperience?.start_date).format("yyyy-MM-DD")
      )
    );

  const [
    createReferral,
    { data: referralData, error: referralError, isLoading: referralLoading },
  ] = useCreateReferralMutation();

  const { data: availableDates, error } =
    useGetBookableExperienceAvailabilityByItemIdQuery(itemId || "");

  useEffect(() => {
    const newCurrentTime = availableDates?.data?.find(
      (time: ItemBookableExperience) => time.id === parseInt(timeId || "")
    );
    setCurrentBookableExperience(
      newCurrentTime || removeExpiredTimes(availableDates?.data)[0]
    );
  }, [timeId, availableDates]);

  // useEffect(() => {
  //   createReferral({
  //     user_id: user.id,
  //     id: parseInt(itemId || ""),
  //     bookableExperienceId: parseInt(timeId || ""),
  //   });
  // }, [itemId, timeId]);

  useEffect(() => {
    setReferralLink(`/checkout/${referralData?.data}`);
  }, [referralData]);

  const handleSelectNewDate = (date: Date) => {
    // filter available dates to find the one that matches the selected date
    // set the id associated with the first bookable experience that matches the selected date as a url param
    // this triggers the useEffect above to reset the current time
    const matches =
      availableDates?.data?.filter((time: ItemBookableExperience) => {
        return (
          moment(time.start_date).format("yyyy-MM-DD") ===
          moment(date).format("yyyy-MM-DD")
        );
      }) || [];

    setTodaysBookableExperiences(matches);

    if (matches.length > 0) {
      navigate(`/book/${itemId}/${matches[0].id}`);
    }
    setDatePickerOpen(false);
  };

  const handleClickShare = () => {
    navigator.clipboard.writeText(referralLink || "");
  };

  if (!currentItem) {
    navigate("/home");
    return <></>;
  }

  const renderInfoBoxes = () => (
    <Box
      display={"grid"}
      gridTemplateColumns={"1fr 1fr"}
      gridTemplateRows={"repeat(1fr)"}
      gap={1}
      marginBottom={1}
    >
      <InfoBox icon={<CalendarMonth fontSize="large" />} label={"Date"}>
        <Typography variant={"body1"}>
          {currentBookableExperience &&
            moment(currentBookableExperience.start_date).format(
              "ddd, MMM DD, YYYY"
            )}
        </Typography>
        <Button
          sx={{ textDecoration: "underline", padding: 0 }}
          onClick={() => setDatePickerOpen(true)}
        >
          Change Date
        </Button>
      </InfoBox>

      {!!currentItem?.capacity && currentItem.capacity > 0 && (
        <InfoBox icon={<Person fontSize="large" />} label={"Max Guests"}>
          <Typography>{currentItem.capacity}</Typography>
        </InfoBox>
      )}
      <InfoBox
        icon={
          <LocalOfferOutlined
            fontSize="large"
            sx={{ transform: "scaleX(-1)" }}
          />
        }
        label={"Base Price"}
      >
        <Typography>
          ${currentItem.price} per {currentItem.units}
        </Typography>
      </InfoBox>
      {currentBookableExperience?.referee && (
        <InfoBox icon={<Person fontSize="large" />} label={"Referee"}></InfoBox>
      )}
    </Box>
  );

  return (
    <Box
      width={"100%"}
      display={"flex"}
      justifyContent={"center"}
      marginTop={2}
    >
      <Box
        maxWidth={680}
        display={"flex"}
        justifyContent={"center"}
        flexDirection={"column"}
      >
        <Box display={"flex"} justifyContent={"flex-start"} marginBottom={0.5}>
          <Button
            color="inherit"
            startIcon={<ChevronLeft />}
            component={Link}
            to={`/home?date=${currentBookableExperience?.start_date}`}
          >
            Back
          </Button>
        </Box>
        <img
          src={currentItem?.images[0]}
          alt="Bookable Item Image"
          style={{
            marginBottom: "1rem",
            borderRadius: "8px",
            height: "258px",
            objectFit: "cover",
          }}
        />
        <Typography variant={"h1"}>{currentItem?.name}</Typography>
        <Typography variant={"body1"}>
          {currentItem?.business.business}
        </Typography>
        <Typography
          variant="body1"
          sx={{ textDecoration: "underline", marginBottom: 1.5 }}
          component={"a"}
          target="_blank"
          href={getGoogleMapsLink(currentItem?.business.address1 ?? "")}
        >
          <FmdGood sx={{ fontSize: "17px", marginRight: 0.5 }} />
          {currentItem?.business.address1}
        </Typography>

        {referralLink ? (
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={1}
            marginBottom={1.5}
          >
            <Button
              variant={"contained"}
              color={"primary"}
              sx={{ flexGrow: 1 }}
              onClick={handleClickShare}
              disabled={referralLoading}
            >
              {referralLoading ? (
                <span>
                  <CircularProgress size={16} /> Generating referral link...
                </span>
              ) : (
                <span>Share Link</span>
              )}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              sx={{ flexGrow: 1 }}
              component={"a"}
              target={"_blank"}
              href={referralLink || ""}
              disabled={referralLoading}
            >
              {referralLoading ? (
                <span>
                  <CircularProgress size={16} /> Generating referral link...
                </span>
              ) : (
                <span>Book Now</span>
              )}
            </Button>
          </Box>
        ) : (
          <></>
        )}

        {referralError && (
          <Typography variant="body1" color="error">
            There was an issue generating your referral link... {error.message}
          </Typography>
        )}

        {renderInfoBoxes()}
        <Typography variant="body2" sx={{ marginBottom: 2 }}>
          {currentItem.offering}
        </Typography>

        <Typography variant="h2">Available Times</Typography>
        <Typography variant="body1">Select a time below.</Typography>

        <Box
          display="grid"
          gridTemplateColumns="repeat(5, 1fr)"
          gap={"12px"}
          marginBottom={1}
        >
          {todaysBookableExperiences?.map(
            (time: ItemBookableExperience, i: number) => {
              const _selected = time.id === parseInt(timeId || "");
              return (
                <Button
                  key={i}
                  variant={_selected ? "contained" : "outlined"}
                  color={_selected ? "primary" : "inherit"}
                  onClick={() => {
                    navigate(`/book/${itemId}/${time.id}`);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    border: !_selected
                      ? "1px solid #d6d6da"
                      : "1px solid primary",
                    borderRadius: "8px",
                  }}
                >
                  <Typography variant="body2">
                    {moment(time.start_date).format("h:mm A")}
                  </Typography>
                  <Typography variant="caption">{time.duration} hr</Typography>
                  <Typography variant="caption" sx={{ fontSize: "11px" }}>
                    {time.units} available
                  </Typography>
                </Button>
              );
            }
          )}
        </Box>
        <Box>
          <InfoSection title="Description" content={currentItem?.description} />

          <InfoSection
            title="Additional Info"
            content={currentItem?.additional_info}
          />

          <InfoSection
            title="Cancellation Policy"
            content={currentItem?.cancellation_policy}
          />
        </Box>
      </Box>
      <DatePickerModal
        open={datePickerOpen}
        date={new Date(currentBookableExperience?.start_date || "")}
        setDate={handleSelectNewDate}
        onClose={() => setDatePickerOpen(false)}
        bookableDates={
          availableDates?.data
            ?.map((time: ItemBookableExperience) => {
              return moment(time.start_date).format("yyyy-MM-DD");
            })
            .reduce(
              (acc: string[], curr: string) =>
                acc.includes(curr) ? acc : [...acc, curr],
              []
            ) || []
        }
      />
    </Box>
  );
};

export default BookableItemPage;
